import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilCallback } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { getDoseLabel } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/DoseStep/doseUtils';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { StructureSet } from '~/domain/patient/structureset';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { readSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { secondaryImageIdState } from '~/state/atoms/image/imageAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import { handleError } from '~/utils/errors';
import useStructureSetActivation from './useStructureSetActivation';

/** The picks that ask first when they change what the user did not pick. */
export interface ConfirmedSelection {
    /** Loads `dose`, asking first when it changes the active set or the image. */
    readonly pickDose: (dose: DoseMatrix) => void;
    /** Activates `set`, asking first when it changes the image. */
    readonly pickStructureSet: (set: AvailableStructureSetView) => void;
}

export const ConfirmedSelectionContext =
    createContext<ConfirmedSelection | null>(null);

/**
 * A pick waiting for the user's answer, with whether a blended image is
 * shown, which an image change removes.
 */
type PendingPick =
    | {
          readonly kind: 'dose';
          readonly dose: DoseMatrix;
          readonly structureSet: StructureSet;
          readonly changesImage: boolean;
          readonly blended: boolean;
      }
    | {
          readonly kind: 'structureSet';
          readonly set: AvailableStructureSetView;
          readonly blended: boolean;
      };

/**
 * Holds the one question a dataset pick asks, for every place that picks a
 * dose or a structure set: the dataset menus and lists, the row footer and
 * a finished dose prediction. A pick asks before it changes something the
 * user did not pick, and Cancel changes nothing:
 * - a structure set on another image than the primary image asks, because
 *   the image changes with it;
 * - a dose asks when its structure set is not the active one, because the
 *   set changes with it, and when that set is on another image.
 * The question names each change, the removal of a blended image included
 * when one is shown. A pick reads the selection of the moment it runs, so
 * a pick started long after it was set up, as a finished job's is, asks
 * about the selection the user has then. Datasets are named masked as
 * patient information.
 */
export default function ConfirmedSelectionProvider({
    children,
}: {
    children: ReactNode;
}) {
    const intl = useIntl();
    const { setEnvironmentDose } = useViewportEnvironment();
    const setEnvironmentDoseRef = useRef(setEnvironmentDose);
    useEffect(() => {
        setEnvironmentDoseRef.current = setEnvironmentDose;
    });
    const { activate } = useStructureSetActivation();
    const [pending, setPending] = useState<PendingPick | undefined>();

    const readSelection = useRecoilCallback(
        ({ snapshot }) =>
            () => ({
                patientId: snapshot
                    .getLoadable(selectedPatientIdState)
                    .getValue(),
                activeStructureSetId: snapshot
                    .getLoadable(currentStructureSetIdState)
                    .getValue(),
                primaryImage: readSelectedPrimaryImage(snapshot),
                blended:
                    snapshot.getLoadable(secondaryImageIdState).getValue() !==
                    null,
            }),
        []
    );

    /** Whether showing the image `seriesUid` replaces the primary image. */
    const changesPrimaryImage = useCallback(
        (seriesUid: string | undefined) => {
            const { primaryImage } = readSelection();
            return (
                seriesUid !== undefined &&
                primaryImage !== undefined &&
                seriesUid !== primaryImage.seriesUid
            );
        },
        [readSelection]
    );

    const loadDose = useCallback(
        (dose: DoseMatrix) =>
            setEnvironmentDoseRef.current(
                [dose],
                readSelection().patientId ?? ''
            ),
        [readSelection]
    );

    const askBeforeImageChange = useCallback(
        (set: AvailableStructureSetView) => {
            if (!changesPrimaryImage(set.imageSeriesUid)) {
                return false;
            }
            setPending({
                kind: 'structureSet',
                set,
                blended: readSelection().blended,
            });
            return true;
        },
        [changesPrimaryImage, readSelection]
    );

    const activateStructureSet = useCallback(
        (set: AvailableStructureSetView) => {
            activate(set, askBeforeImageChange).catch((error) =>
                handleError(error, 'Failed to select the structure set.')
            );
        },
        [activate, askBeforeImageChange]
    );

    const pickStructureSet = useCallback(
        (set: AvailableStructureSetView) => {
            if (!askBeforeImageChange(set)) {
                activateStructureSet(set);
            }
        },
        [askBeforeImageChange, activateStructureSet]
    );

    const pickDose = useCallback(
        (dose: DoseMatrix) => {
            const ownSet = dose.structureSet;
            if (!ownSet) {
                loadDose(dose);
                return;
            }
            const { activeStructureSetId, blended } = readSelection();
            const changesSet =
                activeStructureSetId !== undefined &&
                ownSet.dicomUid !== activeStructureSetId;
            const changesImage = changesPrimaryImage(ownSet.image?.seriesUid);
            if (changesSet || changesImage) {
                setPending({
                    kind: 'dose',
                    dose,
                    structureSet: ownSet,
                    changesImage,
                    blended,
                });
                return;
            }
            loadDose(dose);
        },
        [loadDose, readSelection, changesPrimaryImage]
    );

    const selection = useMemo<ConfirmedSelection>(
        () => ({ pickDose, pickStructureSet }),
        [pickDose, pickStructureSet]
    );

    const blendedValue = pending?.blended ? 'yes' : 'no';

    return (
        <ConfirmedSelectionContext.Provider value={selection}>
            {children}
            {pending?.kind === 'dose' ? (
                <ConfirmationDialog
                    open
                    title={intl.formatMessage({
                        id: pending.changesImage
                            ? 'dose.switchImage.title'
                            : 'dose.switchStructureSet.title',
                    })}
                    message={
                        <FormattedMessage
                            id={
                                pending.changesImage
                                    ? 'dose.switchImage.message'
                                    : 'dose.switchStructureSet.message'
                            }
                            values={{
                                dose: (
                                    <SensitiveInfoTypography>
                                        {getDoseLabel(
                                            pending.dose.plan?.label,
                                            pending.dose.doseSummationType
                                        )}
                                    </SensitiveInfoTypography>
                                ),
                                structureSet: (
                                    <SensitiveInfoTypography>
                                        {pending.structureSet.structureSetLabel}
                                    </SensitiveInfoTypography>
                                ),
                                blended: blendedValue,
                            }}
                        />
                    }
                    confirmText={intl.formatMessage({ id: 'dose.load' })}
                    onConfirm={() => {
                        setPending(undefined);
                        loadDose(pending.dose);
                    }}
                    onCancel={() => setPending(undefined)}
                />
            ) : null}
            {pending?.kind === 'structureSet' ? (
                <ConfirmationDialog
                    open
                    title={<FormattedMessage id="confirmImageChange.title" />}
                    message={
                        <FormattedMessage
                            id="confirmImageChange.message"
                            values={{ blended: blendedValue }}
                        />
                    }
                    onConfirm={() => {
                        setPending(undefined);
                        activateStructureSet(pending.set);
                    }}
                    onCancel={() => setPending(undefined)}
                    mode="primary"
                />
            ) : null}
        </ConfirmedSelectionContext.Provider>
    );
}
